import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './components/ui/card';
import { Alert, AlertDescription, AlertTitle } from './components/ui/alert';
import { Calendar } from 'lucide-react';
import { CitationLink } from './components/ui/citation';
import { Paper, Box, Stack, Grid, Title as MantineTitle, Text, Group, Tabs, Container, Space, NumberInput, Button } from '@mantine/core';
import {
    citations,
    initialProjectedUnitPriceDefault,
    initialProjectedMonthlyRentDefault,
    initialAgentPriceDefault,
    initialActualMonthlyRentDefault,
    IconMapping,
    initialTaxRateDefault
} from './data';
import OverviewPanel from './components/panels/OverviewPanel';
import PriceTrendPanel from './components/panels/PriceTrendPanel';
import MarketAnalysisPanel from './components/panels/MarketAnalysisPanel';
import InvestmentAnalysisPanel from './components/panels/InvestmentAnalysisPanel';
import RentalResearchPanel from './components/panels/RentalResearchPanel';
import RecommendationPanel from './components/panels/RecommendationPanel';
import SellingStudyPanel from './components/panels/SellingStudyPanel';

const FangcunCommercialPropertyReport = () => {
  const [activeTab, setActiveTab] = useState<string | null>('overview');

  // State for editable fields
  const [editableProjectedUnitPrice, setEditableProjectedUnitPrice] = useState<number | ''>(initialProjectedUnitPriceDefault);
  const [editableProjectedMonthlyRent, setEditableProjectedMonthlyRent] = useState<number | ''>(initialProjectedMonthlyRentDefault);
  const [editableAgentPrice, setEditableAgentPrice] = useState<number | ''>(initialAgentPriceDefault);
  const [editableActualMonthlyRent, setEditableActualMonthlyRent] = useState<number | ''>(initialActualMonthlyRentDefault);
  const [editableTaxRate, setEditableTaxRate] = useState<number | ''>(initialTaxRateDefault);

  // useEffect(() => { // Removed, Mantine handles theme
  //   const isDarkMode = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
  //   setDarkMode(isDarkMode);
  // }, []);

  return (
    <Container fluid p="md">
      <Stack>
        <Box ta="center" mb="xl">
          <MantineTitle order={1} mb="sm">廣州市荔灣區芳村商業地產價格走勢分析報告</MantineTitle>
          <Text c="dimmed" size="lg">
             37.5平方米小區商舖投資策略分析
          </Text>
          <Group justify="center" mt="xs">
            <Calendar size={16} style={{ marginRight: '4px' }} />
            <Text c="dimmed" size="sm">報告生成日期: 2025年5月17日</Text>
          </Group>
        </Box>

        <Tabs value={activeTab} onChange={setActiveTab} mb="lg">
          <Tabs.List>
            <Tabs.Tab value="overview">概覽</Tabs.Tab>
            <Tabs.Tab value="price-trend">價格走勢</Tabs.Tab>
            <Tabs.Tab value="market-analysis">市場分析</Tabs.Tab>
            <Tabs.Tab value="investment-analysis">投資分析</Tabs.Tab>
            <Tabs.Tab value="rental-research">租金市場調研</Tabs.Tab>
            <Tabs.Tab value="selling-study">賣盤研究</Tabs.Tab>
            <Tabs.Tab value="recommendation">建議與結論</Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="overview" pt="xs">
            <OverviewPanel 
                editableProjectedUnitPrice={editableProjectedUnitPrice}
                setEditableProjectedUnitPrice={setEditableProjectedUnitPrice}
                editableProjectedMonthlyRent={editableProjectedMonthlyRent}
                setEditableProjectedMonthlyRent={setEditableProjectedMonthlyRent}
                editableAgentPrice={editableAgentPrice}
                setEditableAgentPrice={setEditableAgentPrice}
                editableActualMonthlyRent={editableActualMonthlyRent}
                setEditableActualMonthlyRent={setEditableActualMonthlyRent}
                editableTaxRate={editableTaxRate}
                setEditableTaxRate={setEditableTaxRate}
            />
          </Tabs.Panel>

          <Tabs.Panel value="price-trend" pt="xs">
            <PriceTrendPanel />
          </Tabs.Panel>
          
          <Tabs.Panel value="market-analysis" pt="xs">
            <MarketAnalysisPanel />
          </Tabs.Panel>

          <Tabs.Panel value="investment-analysis" pt="xs">
            <InvestmentAnalysisPanel 
              agentRecommendedPrice={Number(editableAgentPrice) * 1000000}
              actualMonthlyRent={Number(editableActualMonthlyRent)}
              taxRate={Number(editableTaxRate)}
            />
          </Tabs.Panel>

          <Tabs.Panel value="rental-research" pt="xs">
            <RentalResearchPanel />
          </Tabs.Panel>

          <Tabs.Panel value="selling-study" pt="xs">
            <SellingStudyPanel />
          </Tabs.Panel>

          <Tabs.Panel value="recommendation" pt="xs">
            <RecommendationPanel 
              agentRecommendedPrice={Number(editableAgentPrice) * 1000000}
              currentMonthlyRent={Number(editableActualMonthlyRent)}
              taxRate={Number(editableTaxRate)}
            />
          </Tabs.Panel>
        </Tabs>
        
        {/* 引用來源 */}
        <Card mt="xl">
          <CardContent p="lg">
            <MantineTitle order={5} mb="xs">參考資料：</MantineTitle>
            <Stack gap="xs">
                <li><CitationLink id="1" callType="recommend" citations={citations}/></li>
                <li><CitationLink id="2" callType="recommend" citations={citations}/></li>
                <li><CitationLink id="3" callType="recommend" citations={citations}/></li>
                <li><CitationLink id="4" callType="recommend" citations={citations}/></li>
                <li><CitationLink id="5" callType="recommend" citations={citations}/></li>
                <li><CitationLink id="6" callType="recommend" citations={citations}/></li>
                <li><CitationLink id="7" callType="recommend" citations={citations}/></li>
                <li><CitationLink id="8" callType="recommend" citations={citations}/></li>
                <li><CitationLink id="9" callType="recommend" citations={citations}/></li>
                <li><CitationLink id="10" callType="recommend" citations={citations}/></li>
                <li><CitationLink id="11" callType="recommend" citations={citations}/></li>
            </Stack>
          </CardContent>
        </Card>
      </Stack>
    </Container>
  );
};

// New component for the /gzresearch route
const GzResearchPage = () => {
  return <FangcunCommercialPropertyReport />;
};

// New component for the root / route
const HomePage = () => {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
      <h1>Welcome! Navigate to /gzresearch to see the report.</h1>
    </div>
  );
 // Alternatively, to show a completely blank page:
 // return null;
 // Or, for a more specific "Not Found" or "Content Moved" experience, you might redirect or render a specific component.
};

const App = () => {
  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/gzresearch" element={<GzResearchPage />} />
      {/* Optional: Redirect any other path to /gzresearch or a 404 page */}
      <Route path="*" element={<Navigate to="/gzresearch" replace />} />
    </Routes>
  );
};

export default App; // Changed default export to App
